import { APP_SCORING_FORMAT, APP_SCORING_PERCENTAGE_RANGES } from '@/constants';
import { cn } from '@/utils';
import { SkillLevelScaleLabels } from './subcomponents/SkillLevelScaleLabels';
import { SkillLevelScaleProgressBar } from './subcomponents/SkillLevelScaleProgressBar';
import { SkillLevelScaleProps } from './types';
import { skillLevelScaleStyles } from './SkillLevelScale.styles';
import { useSkillLevelScale } from './utils/useSkillLevelScale';
import React from 'react';

/**
 * A visual skill level scale component that shows proficiency levels.
 */
export const SkillLevelScale: React.FC<SkillLevelScaleProps> = ({
  className,
  isLoading = false,
  level,
  runtimeRatingCutoffs,
  score = undefined,
  showLabels = true,
  size = 'small',
  useGradient = true,
}) => {
  // =========================
  // Hooks
  // =========================
  const {
    activeIndex,
    containerParams,
    containerRef,
    currentLevel,
    fillWidth,
    getDividerDisplay,
    gradientBackground,
    skillLevels,
    skillLevelsLength,
  } = useSkillLevelScale({ level, runtimeRatingCutoffs, score, useGradient });

  // =========================
  // Render
  // =========================
  return (
    <div
      aria-label={`Skill level scale ${currentLevel ? `showing ${currentLevel} level` : ''}`}
      className={cn(skillLevelScaleStyles({ size }), containerParams, className)}
      data-level={currentLevel}
      data-testid="skill-level-scale"
      ref={containerRef}
      role="img"
    >
      {/* Skill Level Labels */}
      {showLabels && (
        <SkillLevelScaleLabels
          activeIndex={activeIndex}
          isLoading={isLoading}
          skillLevels={skillLevels}
          type="skill-levels"
        />
      )}

      {/* Progress bar */}
      <SkillLevelScaleProgressBar
        currentLevel={currentLevel}
        fillWidth={fillWidth}
        getDividerDisplay={getDividerDisplay}
        gradientBackground={gradientBackground}
        isLoading={isLoading}
        level={level}
        size={size}
        skillLevelsLength={skillLevelsLength}
        useGradient={useGradient}
      />

      {/* Percentage Labels */}
      {showLabels && APP_SCORING_FORMAT === 'percentage' && (
        <SkillLevelScaleLabels
          activeIndex={activeIndex}
          isLoading={isLoading}
          runtimeRatingCutoffs={runtimeRatingCutoffs}
          skillLevels={skillLevels}
          type="percentages"
        />
      )}
    </div>
  );
};

SkillLevelScale.displayName = 'SkillLevelScale';

export default SkillLevelScale;
